import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users as UsersIcon, RefreshCw, Search, Download, X, BadgeCheck, BadgeX,
  Edit3, Trash2, Mail as MailIcon, Phone as PhoneIcon, Inbox, Package, Calendar, ExternalLink,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import EditMemberModal from './EditMemberModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return String(s).slice(0, 10); }
};

const MembersManager = () => {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [newsletter, setNewsletter] = useState('all');  // all | yes | no
  const [active, setActive] = useState('all');           // all | yes | no
  const [hasBookings, setHasBookings] = useState('all'); // all | yes | no
  const [hasLeads, setHasLeads] = useState('all');       // all | yes | no
  const [editing, setEditing] = useState(null);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (newsletter !== 'all') params.newsletter = newsletter;
      if (active !== 'all') params.active = active;
      if (hasBookings !== 'all') params.has_bookings = hasBookings === 'yes';
      if (hasLeads !== 'all') params.has_leads = hasLeads === 'yes';
      const res = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setMembers(res.data || []);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchMembers(); }, [newsletter, active, hasBookings, hasLeads]);

  const visible = useMemo(() => {
    if (!search.trim()) return members;
    const q = search.trim().toLowerCase();
    return members.filter((m) =>
      [m.name, m.email, m.phone].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [members, search]);

  const stats = useMemo(() => ({
    total: members.length,
    active: members.filter((m) => m.is_active !== false).length,
    inactive: members.filter((m) => m.is_active === false).length,
    newsletter: members.filter((m) => m.newsletter_subscribed).length,
    booked: members.filter((m) => m.booking_count > 0).length,
  }), [members]);

  const exportCsv = async (newsletterOnly) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/customers-export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { newsletter_only: newsletterOnly },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = newsletterOnly ? 'chimba-members-newsletter.csv' : 'chimba-members.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${newsletterOnly ? 'newsletter list' : 'all members'}`);
    } catch {
      toast.error('Export failed');
    }
  };

  const exportNewsletterMaster = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/newsletter-export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'chimba-newsletter-distribution.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Exported full distribution list (members + non-members)');
    } catch {
      toast.error('Export failed');
    }
  };

  const quickToggle = async (m, field) => {
    try {
      const token = localStorage.getItem('token');
      const next = !m[field];
      await axios.put(`${API}/admin/customers/${m.id}`, { [field]: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers((arr) => arr.map((x) => (x.id === m.id ? { ...x, [field]: next } : x)));
      toast.success(`${field === 'is_active' ? 'Active' : 'Newsletter'} → ${next ? 'on' : 'off'}`);
    } catch {
      toast.error('Update failed');
    }
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Permanently delete ${m.name} (${m.email})? Their bookings and leads will be retained for records.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/customers/${m.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers((arr) => arr.filter((x) => x.id !== m.id));
      toast.success('Member deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div data-testid="members-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <h2 className="text-3xl font-heading font-bold text-maroon flex items-center gap-2">
            <UsersIcon className="text-terracotta" size={28} />
            Members
          </h2>
          <p className="text-maroon/60 text-sm mt-1">All registered customers — click a row to view their full profile, bookings, quotes and consultations.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={fetchMembers} variant="outline" className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-2" data-testid="members-refresh-btn">
            <RefreshCw size={16} /> Refresh
          </Button>
          <Button onClick={() => exportCsv(false)} variant="outline" className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-2" data-testid="members-export-all-btn">
            <Download size={16} /> All members CSV
          </Button>
          <Button onClick={() => exportCsv(true)} variant="outline" className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-2" data-testid="members-export-newsletter-btn">
            <Download size={16} /> Newsletter members CSV
          </Button>
          <Button onClick={exportNewsletterMaster} className="btn-primary inline-flex items-center gap-2" data-testid="members-export-newsletter-master-btn">
            <Download size={16} /> Newsletter distribution
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {[
          ['Total', stats.total],
          ['Active', stats.active],
          ['Inactive', stats.inactive],
          ['Newsletter', stats.newsletter],
          ['Booked', stats.booked],
        ].map(([label, val]) => (
          <div key={label} className="card !p-4 text-center">
            <p className="text-2xl font-heading font-bold text-maroon" data-testid={`members-stat-${label.toLowerCase()}`}>{val}</p>
            <p className="text-xs text-maroon/60 uppercase tracking-wider">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-sand/30 border border-maroon/10 rounded-sm p-4 mb-6 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative lg:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-maroon/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, or phone"
              className="w-full pl-9 pr-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
              data-testid="members-search"
            />
          </div>
          <select value={newsletter} onChange={(e) => setNewsletter(e.target.value)} className="px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none" data-testid="members-filter-newsletter">
            <option value="all">Newsletter: all</option>
            <option value="yes">Newsletter: subscribed</option>
            <option value="no">Newsletter: not subscribed</option>
          </select>
          <select value={active} onChange={(e) => setActive(e.target.value)} className="px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none" data-testid="members-filter-active">
            <option value="all">Status: all</option>
            <option value="yes">Active only</option>
            <option value="no">Inactive only</option>
          </select>
          <select value={hasBookings} onChange={(e) => setHasBookings(e.target.value)} className="px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none" data-testid="members-filter-bookings">
            <option value="all">Bookings: any</option>
            <option value="yes">Has bookings</option>
            <option value="no">No bookings</option>
          </select>
          <select value={hasLeads} onChange={(e) => setHasLeads(e.target.value)} className="px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none" data-testid="members-filter-leads">
            <option value="all">Quotes: any</option>
            <option value="yes">Has quote requests</option>
            <option value="no">No quote requests</option>
          </select>
          {(newsletter !== 'all' || active !== 'all' || hasBookings !== 'all' || hasLeads !== 'all' || search) && (
            <button onClick={() => { setSearch(''); setNewsletter('all'); setActive('all'); setHasBookings('all'); setHasLeads('all'); }} className="text-xs text-maroon/60 hover:text-maroon underline inline-flex items-center gap-1" data-testid="members-clear-filters">
              <X size={12} /> Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-maroon/60">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="card text-center py-12">
          <UsersIcon size={48} className="mx-auto text-maroon/20 mb-3" />
          <p className="text-maroon/60">No members match your filters.</p>
        </div>
      ) : (
        <div className="card !p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sand/30 border-b border-maroon/10">
              <tr className="text-left text-maroon/70">
                <th className="px-4 py-3 font-semibold">Member</th>
                <th className="px-4 py-3 font-semibold">Newsletter</th>
                <th className="px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3 font-semibold text-center" title="Bookings"><Package size={14} className="inline" /></th>
                <th className="px-4 py-3 font-semibold text-center" title="Quote requests"><Inbox size={14} className="inline" /></th>
                <th className="px-4 py-3 font-semibold text-center" title="Consultations"><Calendar size={14} className="inline" /></th>
                <th className="px-4 py-3 font-semibold">Joined</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((m) => (
                <tr key={m.id} className="border-b border-maroon/5 hover:bg-sand/20 transition-colors" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/customers/${m.id}`} className="font-semibold text-maroon hover:text-terracotta inline-flex items-center gap-1" data-testid={`member-link-${m.id}`}>
                      {m.name} <ExternalLink size={11} className="text-maroon/30" />
                    </Link>
                    <div className="text-xs text-maroon/50 flex items-center gap-3 mt-0.5">
                      <span className="inline-flex items-center gap-1"><MailIcon size={11} /> {m.email}</span>
                      {m.phone && <span className="inline-flex items-center gap-1"><PhoneIcon size={11} /> {m.phone}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => quickToggle(m, 'newsletter_subscribed')} className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-bold border transition-colors ${m.newsletter_subscribed ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' : 'bg-maroon/5 text-maroon/40 border-maroon/15'}`} data-testid={`member-newsletter-${m.id}`}>
                      {m.newsletter_subscribed ? <><BadgeCheck size={12} /> On</> : <><BadgeX size={12} /> Off</>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => quickToggle(m, 'is_active')} className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-bold border transition-colors ${m.is_active !== false ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' : 'bg-coral/15 text-coral border-coral/30'}`} data-testid={`member-active-${m.id}`}>
                      {m.is_active !== false ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center text-maroon/80">{m.booking_count || 0}</td>
                  <td className="px-4 py-3 text-center text-maroon/80">{m.lead_count || 0}</td>
                  <td className="px-4 py-3 text-center text-maroon/80">{m.consultation_count || 0}</td>
                  <td className="px-4 py-3 text-maroon/60 text-xs">{fmtDate(m.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(m)} className="p-1.5 text-maroon hover:bg-maroon/5 rounded-sm" title="Edit" data-testid={`member-edit-${m.id}`}>
                        <Edit3 size={15} />
                      </button>
                      <button onClick={() => deleteMember(m)} className="p-1.5 text-coral hover:bg-coral/10 rounded-sm" title="Delete" data-testid={`member-delete-${m.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditMemberModal
          userId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setMembers((arr) => arr.map((x) => (x.id === updated.user.id ? {
              ...x,
              name: updated.user.name,
              email: updated.user.email,
              phone: updated.user.phone,
              is_active: updated.user.is_active,
              newsletter_subscribed: updated.user.newsletter_subscribed,
              admin_notes: updated.user.admin_notes,
            } : x)));
            setEditing(null);
            toast.success('Member updated');
          }}
        />
      )}
    </div>
  );
};

export default MembersManager;
